import type { OperationLogModule } from '@/models/operation-log'

/** 一覧「機能」列：API module（小文字スネーク）→ i18n キー（OperationLog.module.*） */
export const OPERATION_LOG_MODULE_I18N_KEY: Record<OperationLogModule, string> = {
  sales: 'OperationLog.module.sales',
  accommodation: 'OperationLog.module.accommodation',
  room: 'OperationLog.module.room',
  setting: 'OperationLog.module.setting',
  cleaning: 'OperationLog.module.cleaning',
  report: 'OperationLog.module.report',
  space: 'OperationLog.module.space',
  survey: 'OperationLog.module.survey',
  mobile_request: 'OperationLog.module.mobile_request',
  smart_key: 'OperationLog.module.smart_key',
  line: 'OperationLog.module.line',
  customer: 'OperationLog.module.customer',
  csv_download: 'OperationLog.module.csv_download',
}

/** 検索「機能」の定義。表示は module ラベル、送信は module slug で統一する。 */
export const OPERATION_LOG_FEATURES = [
  { module: 'accommodation', i18nKey: 'OperationLog.module.accommodation' },
  { module: 'customer', i18nKey: 'OperationLog.module.customer' },
  { module: 'room', i18nKey: 'OperationLog.module.room' },
  { module: 'sales', i18nKey: 'OperationLog.module.sales' },
  { module: 'mobile_request', i18nKey: 'OperationLog.module.mobile_request' },
  { module: 'space', i18nKey: 'OperationLog.module.space' },
  { module: 'cleaning', i18nKey: 'OperationLog.module.cleaning' },
  { module: 'report', i18nKey: 'OperationLog.module.report' },
  { module: 'survey', i18nKey: 'OperationLog.module.survey' },
  { module: 'smart_key', i18nKey: 'OperationLog.module.smart_key' },
  { module: 'csv_download', i18nKey: 'OperationLog.module.csv_download' },
  { module: 'setting', i18nKey: 'OperationLog.module.setting' },
] as const

const LEGACY_FEATURE_FILTER_VALUE_TO_MODULE: Record<string, OperationLogModule> = {
  Sales: 'sales',
  Accommodation: 'accommodation',
  Setting: 'setting',
  Report: 'report',
  Room: 'room',
  Cleaning: 'cleaning',
  Space: 'space',
  Survey: 'survey',
  'Smart Key': 'smart_key',
  LINE: 'line',
  Customer: 'customer',
  'Mobile Request': 'mobile_request',
  'CSV Download': 'csv_download',
}

/** 旧 features 値（Sales など）/ 新値（sales など）を module slug に正規化 */
export function normalizeOperationLogFeatureValue(value: string | undefined): OperationLogModule | undefined {
  if (value == null || value === '') return undefined
  const v = value.trim()
  if (Object.prototype.hasOwnProperty.call(OPERATION_LOG_MODULE_I18N_KEY, v)) return v as OperationLogModule
  const lower = v.toLowerCase()
  if (Object.prototype.hasOwnProperty.call(OPERATION_LOG_MODULE_I18N_KEY, lower)) return lower as OperationLogModule
  return LEGACY_FEATURE_FILTER_VALUE_TO_MODULE[v]
}

/** API の module / フィルタ value から一覧「機能」表示ラベルを取得 */
export function getModuleLabel(moduleValue: string, t: (key: string) => string): string {
  const normalized = normalizeOperationLogFeatureValue(moduleValue)
  if (normalized) {
    return t(OPERATION_LOG_MODULE_I18N_KEY[normalized])
  }
  const found = OPERATION_LOG_FILTER_FEATURES.find(f => f.value === moduleValue)
  return found ? t(found.i18nKey) : moduleValue
}

/** 検索 UI 用。表示は module ラベル、送信値は module slug。 */
export const OPERATION_LOG_FILTER_FEATURES: ReadonlyArray<{ value: OperationLogModule; i18nKey: string }> = OPERATION_LOG_FEATURES.map(
  v => ({ value: v.module, i18nKey: v.i18nKey }),
)

/** @deprecated `OPERATION_LOG_FILTER_FEATURES` を使用 */
export const FEATURES_LIST = OPERATION_LOG_FILTER_FEATURES

/**
 * 操作ログ「テーブル」フィルタの候補。
 * BE の displayable_subject_types と同一（API が返しうる subject_type のみ）。
 */
export const TABLES_LIST = [
  'Reservation',
  'CheckIn',
  'Sale',
  'ReservationGuest',
  'ReservationUser',
  'ReservationNote',
  'ReservationPayment',
  'CheckinGuest',
  'SalesSubject',
  'SalesAccountsReceivable',
  'UploadReceiptLog',
  'Hotel',
  'GuestRoom',
  'AdvanceMoneyHistory',
  'SalesPayment',
  'GuestRoomAssign',
  'GuestRoomAssignPax',
  'SpaceReservedInfo',
  'Company',
  'CustomCheckin',
  'GuestRoomCleanHistory',
] as const
